<script setup>
/* global axios */
// Icerik > Yorumlar: gonderilere gelen yorumlari oku, yanitla, gizle, sil.
//
// Iki sutun: solda yayinda olan gonderiler, sagda secili gonderinin
// yorumlari. Yorum gonderiden bagimsiz bir sey degil; hangi gonderiye
// yazildigini gormeden yanit yazmak, baglami bilmeden konusmak olurdu.
//
// Yorumlar BURADA SAKLANMIYOR: her acilista Instagram'dan taze okunuyor.
// Kopyalasaydik silinen bir yorum bizde durmaya devam eder, temsilci artik
// olmayan bir yoruma yanit yazardi.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { hataMetniniCoz, zamanMetni } from './icerik-ortak';

const { t, locale } = useI18n();
const route = useRoute();

const accountId = computed(() => route.params.accountId);
const uc = computed(() => `/api/v1/accounts/${accountId.value}/icerik/yorumlar`);

const gonderiler = ref([]);
const secili = ref(null);
const yorumlar = ref([]);

// Hesap suzgeci. Birden fazla Instagram hesabi bagliyken kullanici cogu
// zaman TEK bir hesabin yorumlarini goruyor olmak istiyor; hepsini tek
// listede karistirmak hangi yorumun hangi hesaba geldigini gizliyor.
const hesaplar = ref([]);
const seciliHesap = ref('');

// Gonderi basina yorum sayisi. Liste ile BIRLIKTE gelmiyor: her gonderi
// icin ayri bir Meta istegi gerekiyor, beklemek listeyi gec acardi.
const sayilar = ref({});

// Secili gonderinin reklam kopyalari ve okunamayan kopyalar.
const reklamKopyasi = ref([]);
const okunamayan = ref([]);
const reklamEksik = ref(false);

const gonderiYukleniyor = ref(false);
const yorumYukleniyor = ref(false);
const hata = ref('');

// Hangi yorumda islem suruyor: ayni anda iki yoruma dokunmayi engelliyor ve
// dugmede donen halka o satirda cikiyor.
const islemdeki = ref(null);

// Acik yanit kutulari: yorum kimligi -> metin.
const yanitKutulari = ref({});

const gorselHatasi = ref(new Set());
const gorseliGizle = id => {
  gorselHatasi.value = new Set(gorselHatasi.value).add(id);
};

const ozet = metin => {
  const temiz = String(metin || '').trim();
  if (!temiz) return t('ICERIK.METINSIZ');
  return temiz.length > 70 ? `${temiz.slice(0, 70)}…` : temiz;
};

// --- Veri ------------------------------------------------------------------

const hesaplariYukle = async () => {
  try {
    const { data } = await axios.get(`${uc.value}/hesaplar`);
    hesaplar.value = data.hesaplar || [];
  } catch {
    // Suzgec olmadan da calisabiliyoruz; hata gostermeye degmez.
    hesaplar.value = [];
  }
};

// Sayilar liste CIZILDIKTEN sonra isteniyor: gonderi listesi hemen aciliyor,
// sayilar birkac saniye sonra yerine oturuyor.
const sayilariYukle = async () => {
  const istek = gonderiler.value.map(g => ({
    medya_id: g.medya_id,
    inbox_id: g.inbox_id,
  }));
  if (!istek.length) return;

  try {
    const { data } = await axios.post(`${uc.value}/sayilar`, { medya: istek });
    sayilar.value = data.sayilar || {};
  } catch {
    sayilar.value = {};
  }
};

const gonderileriYukle = async () => {
  gonderiYukleniyor.value = true;
  hata.value = '';
  sayilar.value = {};
  try {
    const { data } = await axios.get(`${uc.value}/gonderiler`, {
      params: seciliHesap.value ? { inbox_id: seciliHesap.value } : {},
    });
    gonderiler.value = data.gonderiler || [];
    reklamEksik.value = data.reklam_eksik === true;
    if (gonderiler.value.length) await gonderiSec(gonderiler.value[0]);
    else secili.value = null;
    sayilariYukle();
  } catch (e) {
    hata.value = hataMetniniCoz(e, t('ICERIK.HATA_BILINMEYEN'));
  } finally {
    gonderiYukleniyor.value = false;
  }
};

const hesapDegisti = async () => {
  await gonderileriYukle();
};

const yorumlariYukle = async () => {
  if (!secili.value) return;

  yorumYukleniyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.get(uc.value, {
      params: {
        medya_id: secili.value.medya_id,
        inbox_id: secili.value.inbox_id,
      },
    });
    yorumlar.value = data.yorumlar || [];
    reklamKopyasi.value = data.reklam_kopyasi || [];
    okunamayan.value = data.okunamayan || [];
  } catch (e) {
    yorumlar.value = [];
    reklamKopyasi.value = [];
    okunamayan.value = [];
    hata.value = hataMetniniCoz(e, t('ICERIK.HATA_BILINMEYEN'));
  } finally {
    yorumYukleniyor.value = false;
  }
};

const gonderiSec = async gonderi => {
  secili.value = gonderi;
  yanitKutulari.value = {};
  await yorumlariYukle();
};

// --- Eylemler --------------------------------------------------------------

const yanitKutusunuAc = yorum => {
  yanitKutulari.value = {
    ...yanitKutulari.value,
    [yorum.id]: yanitKutulari.value[yorum.id] ?? '',
  };
};

const yanitKutusunuKapat = yorum => {
  const kalan = { ...yanitKutulari.value };
  delete kalan[yorum.id];
  yanitKutulari.value = kalan;
};

const yanitAcik = yorum =>
  Object.prototype.hasOwnProperty.call(yanitKutulari.value, yorum.id);

const yanitla = async yorum => {
  const metin = String(yanitKutulari.value[yorum.id] || '').trim();
  if (!metin) return;

  islemdeki.value = yorum.id;
  try {
    await axios.post(`${uc.value}/${yorum.id}/yanit`, {
      metin,
      inbox_id: secili.value.inbox_id,
    });
    useAlert(t('ICERIK.YORUM_YANITLANDI'));
    yanitKutusunuKapat(yorum);
    await yorumlariYukle();
  } catch (e) {
    useAlert(hataMetniniCoz(e, t('ICERIK.HATA_BILINMEYEN')));
  } finally {
    islemdeki.value = null;
  }
};

const gizle = async (yorum, gizliMi) => {
  islemdeki.value = yorum.id;
  try {
    await axios.post(`${uc.value}/${yorum.id}/gizle`, {
      gizli: gizliMi,
      inbox_id: secili.value.inbox_id,
    });
    useAlert(gizliMi ? t('ICERIK.YORUM_GIZLENDI') : t('ICERIK.YORUM_GOSTERILDI'));
    await yorumlariYukle();
  } catch (e) {
    useAlert(hataMetniniCoz(e, t('ICERIK.HATA_BILINMEYEN')));
  } finally {
    islemdeki.value = null;
  }
};

const sil = async yorum => {
  // Silme GERI ALINAMAZ ve yorum herkese acik bir seydi; onay soruyoruz.
  if (!window.confirm(t('ICERIK.YORUM_SIL_ONAY'))) return;

  islemdeki.value = yorum.id;
  try {
    await axios.delete(`${uc.value}/${yorum.id}`, {
      params: { inbox_id: secili.value.inbox_id },
    });
    useAlert(t('ICERIK.YORUM_SILINDI'));
    await yorumlariYukle();
  } catch (e) {
    useAlert(hataMetniniCoz(e, t('ICERIK.HATA_BILINMEYEN')));
  } finally {
    islemdeki.value = null;
  }
};

const zaman = deger => (deger ? zamanMetni(deger, locale.value) : '');

// Meta, kullanicinin gizlilik ayarina gore username alanini hic dondurmuyor.
// Ekranda yalniz bir '@' birakmak yerine acik bir yer tutucu yaziyoruz.
const kullaniciAdi = kisi =>
  kisi.kullanici ? `@${kisi.kullanici}` : t('ICERIK.YORUM_KULLANICI_YOK');

const yorumSayisi = gonderi => sayilar.value[gonderi.medya_id];

onMounted(async () => {
  await hesaplariYukle();
  await gonderileriYukle();
});
</script>

<template>
  <div class="flex flex-col gap-4 p-6">
    <div class="flex items-center gap-2">
      <div class="flex flex-col">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('ICERIK.YORUM_BASLIK') }}
        </h2>
        <p class="text-xs text-n-slate-11">{{ t('ICERIK.YORUM_ACIKLAMA') }}</p>
      </div>

      <span class="grow" />

      <select
        v-if="hesaplar.length > 1"
        v-model="seciliHesap"
        class="!mb-0 !w-auto text-sm"
        @change="hesapDegisti"
      >
        <option value="">{{ t('ICERIK.YORUM_TUM_HESAPLAR') }}</option>
        <option v-for="h in hesaplar" :key="h.inbox_id" :value="h.inbox_id">
          {{ h.kullanici ? '@' + h.kullanici : h.ad }}
        </option>
      </select>

      <Button
        size="sm"
        variant="faded"
        icon="i-lucide-refresh-cw"
        :label="t('ICERIK.YENILE')"
        :disabled="yorumYukleniyor || gonderiYukleniyor"
        @click="yorumlariYukle"
      />
    </div>

    <!-- Reklam taramasi eksik kaldiysa sessiz kalmiyoruz: eksik bir sayiyi
         dogruymus gibi gostermek, hic gostermemekten kotu. -->
    <p v-if="reklamEksik" class="text-xs text-n-amber-11">
      {{ t('ICERIK.YORUM_REKLAM_EKSIK') }}
    </p>

    <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

    <div v-if="gonderiYukleniyor" class="flex justify-center p-10">
      <Spinner class="text-n-brand" />
    </div>

    <!-- Yayinda gonderi yoksa yorum da olamaz. -->
    <div
      v-else-if="!gonderiler.length"
      class="max-w-xl p-10 mx-auto text-center border rounded-xl border-n-weak"
    >
      <span
        class="inline-grid mb-3 rounded-full size-10 place-content-center bg-n-alpha-1 text-n-slate-11"
      >
        <span class="i-lucide-message-circle size-5" />
      </span>
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('ICERIK.YORUM_GONDERI_YOK') }}
      </p>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('ICERIK.YORUM_GONDERI_YOK_ACIKLAMA') }}
      </p>
    </div>

    <div v-else class="grid gap-4 lg:grid-cols-3">
      <!-- Gonderi secimi -->
      <ul class="flex flex-col gap-2 list-none lg:max-h-[70vh] lg:overflow-y-auto">
        <li v-for="gonderi in gonderiler" :key="gonderi.medya_id">
          <button
            type="button"
            class="flex w-full gap-3 p-2.5 text-left transition-all border rounded-xl"
            :class="
              secili && secili.medya_id === gonderi.medya_id
                ? 'border-n-brand bg-n-alpha-1'
                : 'border-n-weak bg-n-solid-1 hover:border-n-slate-6'
            "
            @click="gonderiSec(gonderi)"
          >
            <img
              v-if="gonderi.gorsel_url && !gorselHatasi.has(gonderi.medya_id)"
              :src="gonderi.gorsel_url"
              alt=""
              class="object-cover rounded-lg shrink-0 size-12 bg-n-alpha-1"
              @error="gorseliGizle(gonderi.medya_id)"
            />
            <span
              v-else
              class="grid rounded-lg shrink-0 size-12 place-content-center bg-n-alpha-1 text-n-slate-10"
            >
              <span class="i-lucide-image size-4" />
            </span>

            <span class="flex flex-col min-w-0 gap-0.5 grow">
              <span class="text-sm truncate text-n-slate-12">
                {{ ozet(gonderi.metin) }}
              </span>
              <span class="flex flex-wrap items-center gap-1.5">
                <span class="text-xs text-n-slate-11">
                  {{ zaman(gonderi.yayin_zamani) }}
                </span>
                <span
                  v-if="gonderi.reklam_sayisi"
                  class="px-1.5 py-0.5 text-[10px] rounded text-n-teal-11 bg-n-alpha-1"
                >
                  {{ t('ICERIK.YORUM_REKLAMLI') }}
                </span>
              </span>
            </span>

            <!-- Sayac gonderiye ait; "0" ile "henuz yuklenmedi" ayri seyler,
                 bu yuzden tanimsizken hicbir sey yazmiyoruz. -->
            <span
              v-if="yorumSayisi(gonderi) !== undefined"
              class="shrink-0 self-center px-1.5 py-0.5 text-xs rounded-md"
              :class="
                yorumSayisi(gonderi)
                  ? 'text-n-slate-12 bg-n-alpha-2'
                  : 'text-n-slate-10'
              "
            >
              {{ yorumSayisi(gonderi) }}
            </span>
          </button>
        </li>
      </ul>

      <!-- Yorumlar -->
      <section class="lg:col-span-2 flex flex-col gap-3">
        <!-- One cikarilan gonderide yorumlar reklam kopyasinda birikiyor;
             kullanicinin ikisini ayni listede gormesi bekleniyor ama
             nereden geldigini de bilmesi gerekiyor. -->
        <div
          v-if="reklamKopyasi.length"
          class="flex flex-wrap items-center gap-2 px-3 py-2 text-xs border rounded-lg border-n-weak text-n-slate-11"
        >
          <span class="i-lucide-megaphone size-3.5 text-n-teal-11" />
          <span>
            {{ t('ICERIK.YORUM_REKLAM_BILGI', { sayi: reklamKopyasi.length }) }}
          </span>
          <a
            v-for="(k, i) in reklamKopyasi"
            :key="i"
            :href="k.permalink"
            target="_blank"
            rel="noopener"
            class="underline text-n-blue-11"
          >
            {{ k.ad || t('ICERIK.YORUM_REKLAM_KOPYASI') }}
          </a>
        </div>

        <p v-if="okunamayan.length" class="text-xs text-n-amber-11">
          {{ t('ICERIK.YORUM_OKUNAMAYAN', { liste: okunamayan.join(', ') }) }}
        </p>

        <div v-if="yorumYukleniyor" class="flex justify-center p-10">
          <Spinner class="text-n-brand" />
        </div>

        <div
          v-else-if="!yorumlar.length"
          class="p-10 text-center border border-dashed rounded-xl border-n-weak"
        >
          <p class="text-sm text-n-slate-11">{{ t('ICERIK.YORUM_YOK') }}</p>
        </div>

        <ul v-else class="flex flex-col gap-3 list-none">
          <li
            v-for="yorum in yorumlar"
            :key="yorum.id"
            class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak bg-n-solid-1"
            :class="yorum.gizli ? 'opacity-70' : ''"
          >
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ kullaniciAdi(yorum) }}
              </span>
              <span class="text-xs text-n-slate-11">{{ zaman(yorum.zaman) }}</span>
              <span
                v-if="yorum.kaynak === 'reklam'"
                class="px-1.5 py-0.5 text-[10px] rounded text-n-teal-11 bg-n-alpha-1"
                :title="yorum.reklam_adi || ''"
              >
                {{ t('ICERIK.YORUM_REKLAMDAN') }}
              </span>
              <span
                v-if="yorum.gizli"
                class="px-2 py-0.5 text-xs rounded-md text-n-amber-11 bg-n-alpha-1"
              >
                {{ t('ICERIK.YORUM_GIZLI') }}
              </span>
              <span class="grow" />
              <span
                v-if="yorum.begeni"
                class="flex items-center gap-1 text-xs text-n-slate-11"
              >
                <span class="i-lucide-heart size-3.5" />
                {{ yorum.begeni }}
              </span>
            </div>

            <p class="text-sm whitespace-pre-line text-n-slate-12">
              {{ yorum.metin }}
            </p>

            <!-- Yanitlar: yoruma ait, girintili. -->
            <ul
              v-if="yorum.yanitlar.length"
              class="flex flex-col gap-2 pl-4 list-none border-l border-n-weak"
            >
              <li
                v-for="yanit in yorum.yanitlar"
                :key="yanit.id"
                :class="yanit.gizli ? 'opacity-70' : ''"
              >
                <span class="flex flex-wrap items-center gap-2">
                  <span class="text-xs font-medium text-n-slate-12">
                    {{ kullaniciAdi(yanit) }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ zaman(yanit.zaman) }}
                  </span>
                  <span
                    v-if="yanit.gizli"
                    class="px-2 py-0.5 text-xs rounded-md text-n-amber-11 bg-n-alpha-1"
                  >
                    {{ t('ICERIK.YORUM_GIZLI') }}
                  </span>
                </span>
                <p class="text-sm whitespace-pre-line text-n-slate-12">
                  {{ yanit.metin }}
                </p>

                <!-- Yanit da bir yorumdur: gizlenebilir ve silinebilir.
                     "Yanitla" yok; Instagram'da zincir tek seviye. -->
                <span class="flex flex-wrap items-center gap-2 mt-1.5">
                  <Button
                    size="xs"
                    variant="faded"
                    :icon="yanit.gizli ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                    :label="
                      yanit.gizli
                        ? t('ICERIK.YORUM_GOSTER')
                        : t('ICERIK.YORUM_GIZLE')
                    "
                    :is-loading="islemdeki === yanit.id"
                    :disabled="islemdeki === yanit.id"
                    @click="gizle(yanit, !yanit.gizli)"
                  />
                  <Button
                    size="xs"
                    variant="ghost"
                    color="ruby"
                    icon="i-lucide-trash-2"
                    :label="t('ICERIK.SIL')"
                    :disabled="islemdeki === yanit.id"
                    @click="sil(yanit)"
                  />
                </span>
              </li>
            </ul>

            <!-- Yanit kutusu -->
            <div v-if="yanitAcik(yorum)" class="flex flex-col gap-2">
              <textarea
                v-model="yanitKutulari[yorum.id]"
                rows="2"
                class="w-full px-3 py-2 text-sm border rounded-lg resize-y border-n-weak bg-n-solid-1 text-n-slate-12"
                :placeholder="t('ICERIK.YORUM_YANIT_YER')"
              />
              <div class="flex items-center gap-2">
                <Button
                  size="xs"
                  variant="solid"
                  icon="i-lucide-corner-down-right"
                  :label="t('ICERIK.YORUM_YANITLA')"
                  :is-loading="islemdeki === yorum.id"
                  :disabled="
                    islemdeki === yorum.id ||
                    !String(yanitKutulari[yorum.id] || '').trim()
                  "
                  @click="yanitla(yorum)"
                />
                <Button
                  size="xs"
                  variant="ghost"
                  color="slate"
                  :label="t('ICERIK.VAZGEC')"
                  @click="yanitKutusunuKapat(yorum)"
                />
              </div>
            </div>

            <div v-else class="flex flex-wrap items-center gap-2">
              <Button
                size="xs"
                variant="faded"
                icon="i-lucide-corner-down-right"
                :label="t('ICERIK.YORUM_YANITLA')"
                :disabled="islemdeki === yorum.id"
                @click="yanitKutusunuAc(yorum)"
              />
              <!-- Gizlemek silmekten once gelir: geri alinabilir. -->
              <Button
                size="xs"
                variant="faded"
                :icon="yorum.gizli ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                :label="
                  yorum.gizli ? t('ICERIK.YORUM_GOSTER') : t('ICERIK.YORUM_GIZLE')
                "
                :is-loading="islemdeki === yorum.id"
                :disabled="islemdeki === yorum.id"
                @click="gizle(yorum, !yorum.gizli)"
              />
              <span class="grow" />
              <Button
                size="xs"
                variant="ghost"
                color="ruby"
                icon="i-lucide-trash-2"
                :label="t('ICERIK.SIL')"
                :disabled="islemdeki === yorum.id"
                @click="sil(yorum)"
              />
            </div>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
